import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, KeyRound, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const CONSOLE = { in: "https://api-console.zoho.in", com: "https://api-console.zoho.com", eu: "https://api-console.zoho.eu",
  "com.au": "https://api-console.zoho.com.au", jp: "https://api-console.zoho.jp", ca: "https://api-console.zohocloud.ca" };

export const ZOHO_ERRORS = {
  invalid_client: "Zoho did not recognise the Client ID / Secret. Check they were copied from the same data centre's API Console.",
  invalid_code: "The approval code expired. Click Connect Zoho Books again.",
  access_denied: "You clicked Reject on the Zoho screen. Click Connect again and choose Accept.",
  session_expired_try_again: "The connection attempt expired. Click Connect Zoho Books again.",
  client_not_configured: "Save your Zoho Client ID and Secret first.",
  invalid_redirect_uri: "The Redirect URI in Zoho does not match. Copy it exactly from this page into the Zoho API Console.",
};

const Step = ({ n, children }) => (
  <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-bold grid place-items-center">{n}</span>
    <div className="text-sm leading-relaxed">{children}</div></li>
);

const CopyRow = ({ label, value, testid }) => (
  <div className="flex items-center gap-2 mt-1">
    <span className="text-xs text-muted-foreground w-36 shrink-0">{label}</span>
    <code className="text-xs bg-white border rounded-sm px-2 py-1 break-all flex-1" data-testid={testid}>{value}</code>
    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
      data-testid={`${testid}-copy`}><Copy className="w-3.5 h-3.5" /></Button>
  </div>
);

function Guide({ cfg, dc }) {
  const consoleUrl = CONSOLE[dc] || CONSOLE.in;
  return (
    <ol className="space-y-3" data-testid="zoho-setup-steps">
      <Step n={1}>Open the <a href={consoleUrl} target="_blank" rel="noreferrer" className="text-accent underline inline-flex items-center gap-1">Zoho API Console <ExternalLink className="w-3 h-3" /></a> and sign in with the <b>Zoho Books admin</b> login of this company. (India accounts use zoho.in.)</Step>
      <Step n={2}>Click <b>Add Client</b> (or <b>Get Started</b>) and choose <b>Server-based Applications</b>.</Step>
      <Step n={3}>Fill in the form exactly as below and click <b>Create</b>:
        <CopyRow label="Client Name" value="Zenith 360 ERP" testid="zoho-copy-name" />
        <CopyRow label="Homepage URL" value={window.location.origin} testid="zoho-copy-home" />
        <CopyRow label="Authorized Redirect URI" value={cfg.redirect_uri || ""} testid="zoho-copy-redirect" />
      </Step>
      <Step n={4}>Zoho shows a <b>Client ID</b> (starts with 1000.) and a <b>Client Secret</b> on the <b>Client Secret</b> tab. Paste both below, pick the same data centre and click <b>Save</b>.</Step>
      <Step n={5}>Click <b>Connect Zoho Books</b> above → sign in to Zoho → choose your Zoho Books organisation → <b>Accept</b>. You return here showing <b>Connected</b>, then click <b>Sync Now</b>.</Step>
      <li className="text-xs text-muted-foreground border-t pt-2">
        <b>If it fails:</b> “Invalid Redirect URI” → the URI in Zoho must match the one above character for character ·
        “invalid_client” → the keys were created in a different data centre · Using both the preview and live site? Add each site's Redirect URI in the same Zoho client (Zoho allows several).
        The Client Secret is stored encrypted and is never shown again.
      </li>
    </ol>
  );
}

export function ZohoSetupGuide({ onSaved, defaultOpen }) {
  const [cfg, setCfg] = useState({});
  const [form, setForm] = useState({ client_id: "", client_secret: "", dc: "in" });
  const [open, setOpen] = useState(!!defaultOpen);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/zoho/app-config");
    setCfg(r.data);
    setForm({ client_id: r.data.client_id, client_secret: "", dc: r.data.dc });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setOpen(!!defaultOpen); }, [defaultOpen]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/zoho/app-config", form);
      toast.success("Zoho keys saved — now click Connect Zoho Books");
      await load(); onSaved?.(form.dc);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  return (
    <Card className="p-5 rounded-sm" data-testid="zoho-setup-guide">
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)} data-testid="zoho-setup-toggle">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-accent" />
          <span className="font-semibold">Set up the Zoho connection for this company</span>
          <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm ${cfg.has_secret ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
            data-testid="zoho-setup-status">{cfg.has_secret ? "keys saved" : "keys needed"}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="grid lg:grid-cols-2 gap-6 mt-4">
          <Guide cfg={cfg} dc={form.dc} />
          <div className="space-y-3 bg-muted/40 p-4 rounded-sm h-fit">
            <div><Label className="text-xs">Data centre</Label>
              <Select value={form.dc} onValueChange={(v) => setForm({ ...form, dc: v })}>
                <SelectTrigger className="mt-1" data-testid="zoho-setup-dc"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(CONSOLE).map((k) => <SelectItem key={k} value={k}>accounts.zoho.{k}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Client ID</Label>
              <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="1000.XXXXXXXXXXXXXXXX"
                disabled={!cfg.can_edit} className="mt-1 font-mono text-xs" data-testid="zoho-setup-client-id" /></div>
            <div><Label className="text-xs">Client Secret</Label>
              <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder={cfg.has_secret ? "•••••••• saved — leave blank to keep" : "Paste the Client Secret"}
                disabled={!cfg.can_edit} className="mt-1 font-mono text-xs" data-testid="zoho-setup-client-secret" /></div>
            {cfg.can_edit ? (
              <Button onClick={save} disabled={busy} className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="zoho-setup-save">
                {busy ? "Saving…" : "Save"}</Button>
            ) : <div className="text-xs text-amber-700" data-testid="zoho-setup-noperm">Only the owner, CFO or admin can change these keys.</div>}
          </div>
        </div>
      )}
    </Card>
  );
}
